import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { PrivacyNotice } from "./PrivacyNotice";
import {
  ArrowLeft, Send, Loader2, Check, Building2, Brain, Layers,
  BarChart3, HeartPulse, ShoppingBag, Anchor, Leaf
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REQUIREMENT_OPTIONS = [
  "ERP Implementation (Microsoft Dynamics 365 Business Central)",
  "CRM Implementation & Customer Intelligence",
  "Patient Billing & Accounting (CareOps 365)",
  "Data Science & Analytics (Microsoft Fabric)",
  "AI Design Factory — Automation & AI Solutions",
  "Sustainability & ESG Compliance Reporting",
  "System Integration & Data Migration",
  "Upgrade / Migration from Legacy Systems",
  "Regional Multi-Entity ERP Deployment",
  "IT Strategy & Digital Transformation Advisory",
  "Other",
];

const INTEREST_OPTIONS = [
  { label: "ERP & CRM Solutions", icon: Layers },
  { label: "Patient Billing & Accounting", icon: HeartPulse },
  { label: "Data Science & Analytics", icon: Brain },
  { label: "AI Design Factory", icon: Brain },
  { label: "Long-Term Care & Geriatric Care", icon: HeartPulse },
  { label: "Retail & Distribution", icon: ShoppingBag },
  { label: "Marine & Shipping", icon: Anchor },
  { label: "Sustainability & ESG", icon: Leaf },
  { label: "Other", icon: Building2 },
];

export default function ConsultationPage() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    phone: "",
    website: "",
    requirement: "",
    interests: [],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleInterest = (label) => {
    setFormData((prev) => ({
      ...prev,
      interests: prev.interests.includes(label)
        ? prev.interests.filter((i) => i !== label)
        : [...prev.interests, label],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error("Please fill in your first name, last name, and email.");
      return;
    }
    if (!privacyConsent) {
      toast.error("Please agree to the privacy policy to proceed.");
      return;
    }
    setLoading(true);
    try {
      const messageParts = [];
      if (formData.company) messageParts.push(`Company: ${formData.company}`);
      if (formData.phone) messageParts.push(`Phone: ${formData.phone}`);
      if (formData.website) messageParts.push(`Website: ${formData.website}`);
      if (formData.requirement) messageParts.push(`Requirement: ${formData.requirement}`);
      if (formData.interests.length > 0) messageParts.push(`Interests: ${formData.interests.join(", ")}`);
      if (formData.message) messageParts.push(`Message: ${formData.message}`);

      await axios.post(`${API}/enquiries`, {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        message: messageParts.join("\n") || null,
        selected_path: "Strategy Consultation",
      });
      setSubmitted(true);
      toast.success("Thank you! We'll be in touch shortly.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Top Bar */}
      <div className="bg-white border-b border-[#E0D1BB]/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src="/assets/logo-full.svg" alt="Asterisk Computer" className="h-16 w-auto" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#6B6567] hover:text-[#308795] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-16 lg:py-24">
        {!submitted ? (
          <div className="lg:grid lg:grid-cols-5 lg:gap-16">
            {/* Left — Context */}
            <div className="lg:col-span-2 mb-12 lg:mb-0">
              <span className="text-sm font-bold text-[#308795] uppercase tracking-widest mb-3 block">
                Strategy Consultation
              </span>
              <h1 className="text-3xl lg:text-4xl font-bold text-[#2C2A2B] leading-tight mb-5">
                Book a Strategy Consultation
              </h1>
              <p className="text-[#6B6567] text-base leading-relaxed mb-8">
                Tell us about your business challenges and goals. Our team will review your enquiry and connect you with the right specialist for a personalised strategy session.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#308795]/[0.08] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#308795]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2C2A2B]">No obligation consultation</p>
                    <p className="text-xs text-[#8A8385]">Free initial strategy discussion</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#7EA13E]/[0.08] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#7EA13E]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2C2A2B]">Speak with domain experts</p>
                    <p className="text-xs text-[#8A8385]">Average 10+ years consultant experience</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#E5BD77]/[0.12] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#A56A3E]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#2C2A2B]">Tailored to your industry</p>
                    <p className="text-xs text-[#8A8385]">38+ years across 15 industries</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-[#E0D1BB]/30 p-8 shadow-sm">
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Name & Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#494546] mb-1.5">
                        First Name <span className="text-[#E95D54]">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all"
                        placeholder="First name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#494546] mb-1.5">
                        Last Name <span className="text-[#E95D54]">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all"
                        placeholder="Last name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#494546] mb-1.5">
                        Email <span className="text-[#E95D54]">*</span>
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {/* Company & Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#494546] mb-1.5">Name of Company</label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all"
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#494546] mb-1.5">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all"
                        placeholder="+65 XXXX XXXX"
                      />
                    </div>
                  </div>

                  {/* Website */}
                  <div>
                    <label className="block text-sm font-medium text-[#494546] mb-1.5">Website</label>
                    <input type="url" value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all" placeholder="https://www.example.com" />
                  </div>

                  {/* Nature of Requirement */}
                  <div>
                    <label className="block text-sm font-medium text-[#494546] mb-1.5">
                      Nature of Requirement
                    </label>
                    <select
                      value={formData.requirement}
                      onChange={(e) => setFormData({ ...formData, requirement: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">Select the nature of your requirement...</option>
                      {REQUIREMENT_OPTIONS.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Interest Areas */}
                  <div>
                    <label className="block text-sm font-medium text-[#494546] mb-3">
                      Areas of Interest <span className="text-xs text-[#8A8385] font-normal">(select all that apply)</span>
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {INTEREST_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const selected = formData.interests.includes(opt.label);
                        return (
                          <button
                            key={opt.label}
                            type="button"
                            onClick={() => toggleInterest(opt.label)}
                            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-xs font-medium transition-all duration-200 text-left ${
                              selected
                                ? "bg-[#308795]/[0.08] border-[#308795]/30 text-[#308795]"
                                : "bg-[#FAFAF7] border-[#E0D1BB]/40 text-[#6B6567] hover:border-[#308795]/20"
                            }`}
                          >
                            <Icon className={`w-3.5 h-3.5 shrink-0 ${selected ? "text-[#308795]" : "text-[#8A8385]"}`} />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-[#494546] mb-1.5">
                      Tell us about your needs
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-[#E0D1BB]/50 bg-white text-[#2C2A2B] placeholder-[#C5BFB9] text-sm focus:outline-none focus:border-[#308795] focus:ring-2 focus:ring-[#308795]/10 transition-all resize-none"
                      placeholder="Describe your business challenges, current systems, or what you'd like to achieve..."
                    />
                  </div>

                  {/* Submit */}
                  <PrivacyNotice checked={privacyConsent} onChange={setPrivacyConsent} />
                  <Button
                    type="submit"
                    disabled={loading || !privacyConsent}
                    className="w-full bg-[#308795] hover:bg-[#267580] text-white font-semibold py-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-[#308795]/20 text-base disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Book Strategy Consultation
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-[#8A8385] text-center">
                    We typically respond within 1 business day.
                  </p>
                </form>
              </div>
            </div>
          </div>
        ) : (
          /* Success State */
          <div className="max-w-lg mx-auto text-center py-16">
            <div className="w-20 h-20 rounded-full bg-[#7EA13E]/15 flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-[#7EA13E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-[#2C2A2B] mb-3">Thank You!</h2>
            <p className="text-[#6B6567] text-lg mb-8">
              We've received your consultation request and will get back to you within 1 business day with a tailored strategy proposal.
            </p>
            <Link to="/">
              <Button className="bg-[#308795] hover:bg-[#267580] text-white font-semibold px-8 py-3 rounded-xl">
                Back to Home
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
